import { SelectInput } from "@ameelio/ui";
import { useMutation } from "@apollo/client/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { OrganizationMembershipRole } from "../api/graphql";
import FormDialog from "../lib/FormDialog";
import useApolloErrorHandler from "../lib/handleApolloError";
import { useCurrentMembership } from "../SessionBoundary";
import { UpdateOrganizationMembershipDocument } from "./UpdateOrganizationMembership.generated";

type Props = {
  organizationMembership: {
    id: string;
    role: OrganizationMembershipRole;
    visitor: { fullName: string };
  };
  onClose: () => void;
  onRemove?: () => void;
};

type FormData = {
  role: OrganizationMembershipRole;
};

export default function EditOrganizationMembershipModal({
  organizationMembership,
  onClose,
  onRemove,
}: Props) {
  const { t } = useTranslation();
  const handleApolloError = useApolloErrorHandler();
  const { id: currentMembershipId } = useCurrentMembership();
  const isSelf = organizationMembership.id === currentMembershipId;

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
    control,
    watch,
  } = useForm<FormData>({
    defaultValues: { role: organizationMembership.role },
  });

  const [updateMembership] = useMutation(UpdateOrganizationMembershipDocument, {
    onError: handleApolloError,
    onCompleted: onClose,
  });

  const onSubmit = handleSubmit(async ({ role }) => {
    await updateMembership({
      variables: {
        input: { organizationMembershipId: organizationMembership.id, role },
      },
    });
  });

  const roleDescription =
    watch("role") === OrganizationMembershipRole.Admin
      ? t(
          "Admins can manage team members and update team details as well as request approved contacts and meetings from partner facilities."
        )
      : t(
          "Team members can request approved contacts and meetings from partner facilities."
        );

  return (
    <FormDialog
      title={t("Edit {{name}}", {
        name: organizationMembership.visitor.fullName,
      })}
      onClose={onClose}
      onSubmit={onSubmit}
      submitting={isSubmitting}
      submitDisabled={!isDirty}
      onDelete={onRemove}
      deleteLabel={t("Remove from team")}
    >
      <SelectInput
        control={control}
        name="role"
        label={t("Role")}
        disabled={isSelf}
        items={[
          {
            value: OrganizationMembershipRole.Member,
            name: t("Team member"),
          },
          {
            value: OrganizationMembershipRole.Admin,
            name: t("Team admin"),
          },
        ]}
        helperText={roleDescription}
      />
    </FormDialog>
  );
}
